import { extraTotalWithCountOnly, extraTotalWithRentalDays } from "@/helpers/totalExtraPrice";
import { ONE_TIME_CHARGE_EXTRA_ITEMS } from "@/lib/constant";
import {
  PickupReturnProps,
  PriceResultProps,
  ProtectionProps,
  PriceProps,
  PriceResultAllExtra,
} from "@/lib/type";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format } from 'date-fns';
import { formatCurrencyAmount } from "@/lib/utils";

interface ReservationTabMenuProps {
  currentStep: number;
  setStep: any;
  filterData?: PickupReturnProps;
  vehicle?: PriceResultProps;
  protection?: ProtectionProps;
  extras?: PriceResultAllExtra[];
  review?: PriceProps;
  rentalDays: number;
  deliveryCount?: number;
  deliveryFee?: number;
}
const MAX_CHARGE_DAYS: Record<string, number> = {
  "NVS": 10,
  "BSEAT": 10,
  "CSEAT": 10,
  "YDS": 4,
};
const ReservationTabMenu: React.FC<ReservationTabMenuProps> = ({
  currentStep,
  setStep,
  filterData,
  vehicle,
  protection,
  extras,
  review,
  rentalDays,
  deliveryCount = 0,
  deliveryFee = 0
}) => {
  return (
    <div className="md:grid md:grid-cols-11 gap-2 bg-neutral-100 md:px-32 p-4 flex flex-row overflow-x-auto overflow-y-visible">
      <div className="md:col-span-4 bg-white rounded-sm p-2 cursor-pointer" onClick={() => setStep(-1)}>
        <div className="flex flex-row justify-between space-x-2 items-center md:w-auto w-max">
          <div className="bg-gray-300 text-white font-bold text-center w-6 rounded-ss-md rounded-ee-md">
            1
          </div>
          <p className="text-neutral-400 font-bold text-sm grow">
            RENTAL LOCATION
          </p>
          <FontAwesomeIcon
            icon={faPen}
            className="text-green-600"
          />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-2 hidden md:grid">
          <div className="flex flex-col">
            <p className="font-bold text-xs">Pick up</p>
            <p className="font-bold line-clamp-1">
              {filterData?.pickUpLocation?.name}
            </p>
            <p className="text-sm text-neutral-500">{`${!!filterData?.pickUpDate?format(filterData?.pickUpDate, 'yyyy-MM-dd'):``} ${filterData?.pickUpTime}`}</p>
          </div>
          {!!filterData?.returnDate && (
                      <div className="flex flex-col">
                      <p className="font-bold text-xs">Return</p>
                      <p className="font-bold line-clamp-1">
                        {filterData?.returnLocation?.name}
                      </p>
                      <p className="text-sm text-neutral-500">{`${!!filterData?.returnDate?format(filterData?.returnDate, 'yyyy-MM-dd'):``} ${filterData?.returnTime}`}</p>
                    </div>
          )}

        </div>
      </div>
      <div
        className={`md:col-span-2 bg-white rounded-sm p-2 flex flex-col justify-between cursor-pointer ${currentStep == 0 ? "border border-green-600" : ""}`}
        onClick={() => setStep(0)}
      >
        <div className="flex flex-row justify-between space-x-2 items-center md:w-auto w-max">
          <div
            className={`text-white font-bold text-center w-6 rounded-ss-md rounded-ee-md ${currentStep == 0 ? "bg-green-600" : "bg-gray-300"}`}
          >
            2
          </div>
          <p
            className={`font-bold text-sm grow ${currentStep == 0 ? "text-green-600" : "text-neutral-400"}`}
          >
            VEHICLE
          </p>
          <FontAwesomeIcon
            icon={faPen}
            className={`${vehicle == undefined || currentStep == 0 ? "text-transparent" : "text-green-600"}`}
          />
        </div>
        <div className="hidden md:flex">
          {vehicle == undefined ? (
            <p className="text-gray-500 text-sm">
              You have not selected a vehicle yet.
            </p>
          ) : (
            <div>
              <p className="font-bold">{vehicle?.group.name}</p>
              <p className="text-neutral-500">
                {formatCurrencyAmount(
                  vehicle?.pricing.total_with_tax ?? 0,
                  vehicle?.pricing.currency || "",
                )}
              </p>
            </div>
          )}
        </div>
      </div>
      <div
        className={`md:col-span-3 bg-white rounded-sm p-2 flex flex-col justify-between cursor-pointer ${currentStep == 1 ? "border border-green-600" : ""}`}
        onClick={() => setStep(1)}
      >
        <div className="flex flex-row justify-between space-x-2 items-center md:w-auto w-max">
          <div
            className={`text-white font-bold text-center w-6 rounded-ss-md rounded-ee-md ${currentStep == 1 ? "bg-green-600" : "bg-gray-300"}`}
          >
            3
          </div>
          <p
            className={`font-bold text-sm grow ${currentStep == 1 ? "text-green-600" : "text-neutral-400"}`}
          >
            PROTECTION, EXTRAS
          </p>
          <FontAwesomeIcon
            icon={faPen}
            className={`${protection == undefined || currentStep == 1 ? "text-transparent" : "text-green-600"}`}
          />
        </div>
        <div className="hidden md:flex">
          {protection == undefined ? (
            <p className="text-gray-500 text-sm">
              You will be able to choose protection and extras after selecting a
              vehicle.
            </p>
          ) : (
            <div className="flex flex-row items-center justify-between space-x-2">
              <div>
                <p className="font-bold">{protection?.name}</p>
                <p className="text-neutral-500 text-sm">
                  {protection?.isInclude ? "Included" : "Not included"}
                </p>
              </div>
              <div
                className={`pl-2 pr-5 border-l-2 ${currentStep == 1 ? "border-green-600" : ""}`}
              >
                <p className="font-bold" data-testid="wizard-extra-count">{(extras??[]).reduce((sum, extra) => sum + (extra.count ?? 0), 0) + deliveryCount} Extra</p>
                <p className="text-neutral-500">
                  {formatCurrencyAmount(
                    extraTotalWithCountOnly((extras ?? []).filter((e)=>ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id))) +
                    extraTotalWithRentalDays((extras ?? []).filter((e)=>!ONE_TIME_CHARGE_EXTRA_ITEMS.includes(e.extra_id)),rentalDays) +
                    (protection.price ?? 0) +
                    deliveryFee,
                    vehicle?.pricing.currency || "",
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className={`md:col-span-2 bg-white rounded-sm p-2 font-bold flex flex-col justify-between ${currentStep == 2 ? "border border-green-600" : ""}`}
      >
        <div className="flex flex-row justify-between space-x-2 items-center md:w-auto w-max">
          <div
            className={`text-white text-center w-6 rounded-ss-md rounded-ee-md ${currentStep == 2 ? "bg-green-600" : "bg-gray-300"}`}
          >
            4
          </div>
          <p
            className={`${currentStep == 2 ? "text-green-600" : "text-neutral-400"} text-sm grow`}
          >
            REVIEW
          </p>
        </div>
        <div className="hidden md:flex">
          {review == undefined ? (
            <></>
          ) : (
            <div>
              <p>Total price</p>
              <p className="text-neutral-500" data-testid="wizard-total">
                {formatCurrencyAmount(review?.price ?? 0, vehicle?.pricing.currency || "")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationTabMenu;
